import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Activity, ArrowRight, Beaker02, Coins01, FileCode01 } from "@untitledui/icons";
import { useTokenStats } from "@/lib/tokens";
import { useMaintenanceStats } from "@/lib/maintenance";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";

function StatCard({ label, value, tone }: { label: string; value: number | string; tone?: "brand" | "success" | "warning" | "error" | "gray" }) {
  const valueColor =
    tone === "success"
      ? "text-success-primary"
      : tone === "brand"
        ? "text-brand-secondary"
        : tone === "warning"
          ? "text-warning-primary"
          : tone === "error"
            ? "text-error-primary"
            : "text-primary";
  return (
    <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <p className="text-sm font-medium text-tertiary">{label}</p>
      <p className={`mt-2 text-display-sm font-semibold ${valueColor}`}>{value}</p>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 rounded-lg bg-secondary px-4 py-3 ring-1 ring-secondary">
      <span className="text-sm text-secondary">{label}</span>
      <span className="text-lg font-semibold text-primary tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Dashboard — souhrn tokenů (počty dle stavu + distribuce dle auth serveru
 * z agregačního endpointu `/admin/api/v1/tokens/stats`), systémové statistiky
 * (transakce/aktivace/komunikační logy z maintenance statistik) a Quick Actions.
 *
 * Počty dřív vznikaly v prohlížeči z celého seznamu tokenů — každé zobrazení
 * Přehledu tak stahovalo tisíce řádků kvůli pěti číslům.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useTokenStats();
  const stats = useMaintenanceStats();

  // Počty agreguje databáze; UI z nich už jen skládá své kategorie — „nepovedené"
  // je součet dvou stavů, který dává smysl jen tady, ne v datech.
  const tokenStats = useMemo(() => {
    const by = (s: string) => data?.byState?.[s] ?? 0;
    return {
      total: data?.total ?? 0,
      active: by("ACTIVE"),
      activating: by("WAITING_FOR_ACTIVATION"),
      failed: by("BLOCKED") + by("TEMPORARY_BLOCKED"),
      deleted: by("DELETED"),
      distribution: Object.entries(data?.byAuthServerType ?? {}).sort((a, b) => b[1] - a[1]),
    };
  }, [data]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("dashboard.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("dashboard.welcome")}</p>
      </div>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          {/* Token stat karty */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label={t("dashboard.tokensTotal")} value={tokenStats.total} tone="brand" />
            <StatCard label={t("dashboard.tokensActive")} value={tokenStats.active} tone="success" />
            <StatCard label={t("dashboard.tokensActivating")} value={tokenStats.activating} tone="warning" />
            <StatCard label={t("dashboard.tokensFailed")} value={tokenStats.failed} tone="error" />
            <StatCard label={t("dashboard.tokensDeleted")} value={tokenStats.deleted} tone="gray" />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Auth Server Distribution */}
            <section className="flex flex-col gap-3">
              <h2 className="text-md font-semibold text-primary">{t("dashboard.authServerDistribution")}</h2>
              <div className="flex flex-col gap-2">
                {tokenStats.distribution.length === 0 ? (
                  <p className="text-sm text-tertiary">{t("dashboard.noData")}</p>
                ) : (
                  tokenStats.distribution.map(([type, count]) => <MiniStat key={type} label={type} value={count} />)
                )}
              </div>
            </section>

            {/* System Statistics */}
            <section className="flex flex-col gap-3">
              <h2 className="text-md font-semibold text-primary">{t("dashboard.systemStats")}</h2>
              {stats.isLoading ? (
                <LoadingIndicator />
              ) : (
                <div className="flex flex-col gap-2">
                  <MiniStat label={t("dashboard.systemStats.transactions")} value={stats.data?.transactionLogsTotal ?? 0} />
                  <MiniStat label={t("dashboard.systemStats.activationEvents")} value={stats.data?.activationEventsTotal ?? 0} />
                  <MiniStat label={t("dashboard.systemStats.commLogs")} value={stats.data?.httpLogsTotal ?? 0} />
                </div>
              )}
            </section>
          </div>

          {/* Quick Actions */}
          <section className="flex flex-col gap-3">
            <h2 className="text-md font-semibold text-primary">{t("dashboard.quickActions")}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <QuickAction to="/tokens" icon={Coins01} label={t("dashboard.quick.viewTokens")} />
              <QuickAction to="/testing/activation" icon={Beaker02} label={t("dashboard.quick.testActivation")} />
              <QuickAction href="/swagger-ui.html" icon={FileCode01} label={t("dashboard.quick.apiDocs")} />
              <QuickAction href="/actuator/health" icon={Activity} label={t("dashboard.quick.health")} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function QuickAction({
  to,
  href,
  icon: Icon,
  label,
}: {
  to?: string;
  href?: string;
  icon: typeof Coins01;
  label: string;
}) {
  const inner = (
    <div className="flex items-center gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary transition hover:bg-primary_hover">
      <Icon className="size-5 text-brand-secondary" />
      <span className="flex-1 text-sm font-medium text-secondary">{label}</span>
      <ArrowRight className="size-4 text-tertiary" />
    </div>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {inner}
    </a>
  );
}
